import { useEffect } from "react";
import { formatCents } from "../lib/money";
import { useGrow } from "../lib/useGrow";

/** Milliseconds for each of the two phases of the animation. */
const GROW_TOTAL_MS = 700;
const GROW_SLICE_MS = 900;
/** A beat between the two phases, so they read as separate steps. */
const PHASE_GAP_MS = 220;

const RADIUS = 52;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * Confirms a purchase before it happens, showing what it costs as a share of
 * the child's spending money.
 *
 * The chart animates in two steps on purpose. First the whole Spend balance
 * sweeps out a full circle — "this is everything you have to spend". Then the
 * cost grows into its slice of that circle — "this is how much of it this
 * costs". Showing the two at once would be a statistic; showing them in
 * sequence is an explanation.
 */
export function SpendConfirm({
  amountCents,
  spendBalanceCents,
  note,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  amountCents: number;
  spendBalanceCents: number;
  note: string;
  busy: boolean;
  error: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // Phase 1: the whole balance. Phase 2: the slice being spent.
  const totalProgress = useGrow(GROW_TOTAL_MS);
  const sliceProgress = useGrow(GROW_SLICE_MS, GROW_TOTAL_MS + PHASE_GAP_MS);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, busy]);

  const share =
    spendBalanceCents > 0
      ? Math.min(1, amountCents / spendBalanceCents)
      : amountCents > 0
        ? 1
        : 0;
  const remaining = spendBalanceCents - amountCents;
  const sharePercent = Math.round(share * 100 * sliceProgress);

  return (
    <div className="spend-backdrop" role="presentation">
      <div
        className="spend-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`Spend ${formatCents(amountCents)}?`}
      >
        <h2 className="spend-dialog__title">Spend {formatCents(amountCents)}?</h2>
        {note ? <p className="spend-dialog__note">for {note}</p> : null}

        <div className="spend-chart">
          <svg viewBox="0 0 120 120" role="img" aria-label={
            `${formatCents(amountCents)} is ${Math.round(share * 100)}% of your ` +
            `${formatCents(spendBalanceCents)} spending money`
          }>
            {/* Track: the faint full circle the balance grows along. */}
            <circle
              cx="60"
              cy="60"
              r={RADIUS}
              fill="none"
              stroke="var(--c-border)"
              strokeWidth="14"
              opacity="0.35"
            />

            {/* Phase 1 — the whole Spend balance, sweeping to a full circle. */}
            <circle
              cx="60"
              cy="60"
              r={RADIUS}
              fill="none"
              stroke="var(--c-spend)"
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={`${CIRCUMFERENCE * totalProgress} ${CIRCUMFERENCE}`}
              transform="rotate(-90 60 60)"
            />

            {/* Phase 2 — the slice being spent, drawn over the balance. */}
            <circle
              cx="60"
              cy="60"
              r={RADIUS}
              fill="none"
              stroke="var(--c-negative)"
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={`${CIRCUMFERENCE * share * sliceProgress} ${CIRCUMFERENCE}`}
              transform="rotate(-90 60 60)"
            />

            <text
              x="60"
              y="56"
              textAnchor="middle"
              className="spend-chart__percent"
            >
              {sharePercent}%
            </text>
            <text
              x="60"
              y="72"
              textAnchor="middle"
              className="spend-chart__caption"
            >
              of Spend
            </text>
          </svg>
        </div>

        <dl className="spend-facts">
          <div>
            <dt>You have</dt>
            <dd>{formatCents(spendBalanceCents)}</dd>
          </div>
          <div>
            <dt>This costs</dt>
            <dd className="spend-facts__cost">−{formatCents(amountCents)}</dd>
          </div>
          <div>
            <dt>Left after</dt>
            <dd>{formatCents(remaining)}</dd>
          </div>
        </dl>

        {error ? (
          <p className="alert" role="alert">
            {error}
          </p>
        ) : null}

        <div className="row row--between">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Never mind
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Spending…" : "Yes, spend it"}
          </button>
        </div>
      </div>
    </div>
  );
}
